// Phase 8.5-04 UX-02 — UI helper that builds the URL-driven list query string
// per ADR-014 conventions. Backend expects:
//
//   ?filter[<key>]=<value>&sort=<±field>&page=<N>&pageSize=<M>
//
// `openapi-fetch` only types the flat top-level params (`sort`, `page`,
// `pageSize`) — bracketed filters are passed via the per-request
// `querySerializer` option below. Server response shape is
// `{ items, page, pageSize, total }`.
//
// Phase 8.5-05 also reuses the same bracket-format for TanStack Router
// URL state (see `router.tsx` — `parseListQuerySearch` /
// `stringifyListQuerySearch`). One serializer covers both layers.

export interface ListQueryResponse<TItem> {
  items: TItem[];
  page: number;
  pageSize: number;
  total: number;
}

type ListQueryFilter = Record<string, string | number | boolean | undefined>;

const stringifyScalar = (value: string | number | boolean): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  return value ? 'true' : 'false';
};

/**
 * Builds an `openapi-fetch`-compatible querySerializer that emits the bracketed
 * filter syntax + flat sort/page/pageSize. Pass the filter object alongside
 * (the openapi-typed `query` only carries sort/page/pageSize keys).
 */
export const buildListQuerySerializer = (filter?: ListQueryFilter) => {
  return (typedParams: Record<string, unknown>): string => {
    const sp = new URLSearchParams();
    for (const [key, value] of Object.entries(typedParams)) {
      if (value === undefined || value === '') continue;
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        sp.set(key, stringifyScalar(value));
      }
    }
    if (filter) {
      for (const [key, value] of Object.entries(filter)) {
        if (value === undefined || value === '') continue;
        sp.set(`filter[${key}]`, stringifyScalar(value));
      }
    }
    return sp.toString();
  };
};

/**
 * Default page size matching backend ADR-014 default. Callers can override.
 */
export const DEFAULT_PAGE_SIZE = 20;

// Phase 8.5-05 — bracket-format search serializer for TanStack Router so
// URL state matches the backend wire format. Wired globally in
// `router.tsx` via `parseSearchWith` / `stringifySearchWith`. Limited
// schema: scalar top-level keys (`sort`, `page`, `pageSize`) + a single
// nested `filter` object with scalar values. Anything else is passed
// through unchanged.
//
// This is purposely narrower than `qs` (~14kb) — our search shapes are
// uniform across all list screens, so we don't need full nested-object
// support and we don't pay for code that we wouldn't use.
const BRACKET_KEY = /^([a-zA-Z0-9_]+)\[([a-zA-Z0-9_]+)\]$/;

export const stringifyListQuerySearch = (search: Record<string, unknown>): string => {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue;
    if (typeof value === 'object' && !Array.isArray(value)) {
      for (const [k2, v2] of Object.entries(value as Record<string, unknown>)) {
        if (v2 === undefined || v2 === null || v2 === '') continue;
        if (typeof v2 === 'string' || typeof v2 === 'number' || typeof v2 === 'boolean') {
          sp.set(`${key}[${k2}]`, stringifyScalar(v2));
        }
      }
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      sp.set(key, stringifyScalar(value));
    }
  }
  const s = sp.toString();
  if (s === '') return '';
  // URLSearchParams encodes `[` / `]` as `%5B` / `%5D`. RFC 3986 allows
  // them un-encoded in the query component (gen-delims "[" / "]"
  // are reserved but well-defined in query). Decoding keeps URLs
  // readable and matches the canonical bracket-format used in ADR-014
  // examples and the backend's qs parser. Both encodings round-trip
  // through `parseListQuerySearch` identically.
  return `?${s.replace(/%5B/g, '[').replace(/%5D/g, ']')}`;
};

export const parseListQuerySearch = (rawSearch: string): Record<string, unknown> => {
  const trimmed = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch;
  const out: Record<string, unknown> = {};
  for (const [key, value] of new URLSearchParams(trimmed).entries()) {
    const m = BRACKET_KEY.exec(key);
    if (m) {
      const [, root, sub] = m;
      if (!root || !sub) continue;
      const bucket = (out[root] ??= {}) as Record<string, string>;
      bucket[sub] = value;
    } else {
      out[key] = value;
    }
  }
  return out;
};
